// components/branches/config/appointment/DayBuffersSettings.jsx
import React from "react";
import NumberInput from "../../../common/NumberInput";
import { ClockIcon, SunIcon, MoonIcon } from "@heroicons/react/outline";

const DayBuffersSettings = ({ config, onChange, isEditMode }) => {
  const handleStartBufferChange = (field, value) => {
    onChange({
      ...config,
      startBuffer: {
        ...config.startBuffer,
        [field]: value,
      },
    });
  };

  const handleEndBufferChange = (field, value) => {
    onChange({
      ...config,
      endBuffer: {
        ...config.endBuffer,
        [field]: value,
      },
    });
  };

  const handleApplyDaysChange = (day) => {
    const currentDays = config.applyToDays || [
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
      "sunday",
    ];
    const newDays = currentDays.includes(day)
      ? currentDays.filter((d) => d !== day)
      : [...currentDays, day];

    onChange({
      ...config,
      applyToDays: newDays,
    });
  };

  const daysOfWeek = [
    { value: "monday", label: "Mon" },
    { value: "tuesday", label: "Tue" },
    { value: "wednesday", label: "Wed" },
    { value: "thursday", label: "Thu" },
    { value: "friday", label: "Fri" },
    { value: "saturday", label: "Sat" },
    { value: "sunday", label: "Sun" },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
          <ClockIcon className="h-5 w-5 text-blue-600" />
          Day Buffers
        </h3>
        <p className="text-gray-600">
          Configure buffer time at the start and end of each business day for
          setup and closing procedures
        </p>
      </div>

      {/* Visual Timeline Preview */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6">
        <h4 className="font-medium mb-4">Daily Timeline Preview</h4>
        <div className="relative">
          {/* Timeline Bar */}
          <div className="h-12 bg-white rounded-lg shadow-sm flex overflow-hidden">
            {/* Start Buffer */}
            <div
              className="bg-orange-200 flex items-center justify-center border-r-2 border-orange-400"
              style={{
                width: `${Math.min(
                  ((config.startBuffer?.minutes || 0) / 60) * 10,
                  30
                )}%`,
              }}
            >
              <span className="text-xs font-medium text-orange-800">Setup</span>
            </div>

            {/* Available Time */}
            <div className="flex-1 bg-green-100 flex items-center justify-center">
              <span className="text-sm font-medium text-green-800">
                Available for Appointments
              </span>
            </div>

            {/* End Buffer */}
            <div
              className="bg-indigo-200 flex items-center justify-center border-l-2 border-indigo-400"
              style={{
                width: `${Math.min(
                  ((config.endBuffer?.minutes || 0) / 60) * 10,
                  30
                )}%`,
              }}
            >
              <span className="text-xs font-medium text-indigo-800">
                Closing
              </span>
            </div>
          </div>

          {/* Timeline Labels */}
          <div className="flex justify-between mt-2 text-xs text-gray-600">
            <span>Store Opens</span>
            <span>Store Closes</span>
          </div>
        </div>

        {/* Example Calculation */}
        <div className="mt-4 bg-white rounded p-3 text-sm">
          <p className="text-gray-700">
            <strong>Example:</strong> If store hours are 10:00 AM - 8:00 PM
          </p>
          <div className="mt-2 space-y-1">
            <p>
              • First appointment:{" "}
              <span className="font-semibold text-green-700">
                {(() => {
                  const storeOpenHour = 10; // 10 AM
                  const storeOpenMinute = 0;
                  const startBufferMinutes = config.startBuffer?.enabled
                    ? config.startBuffer?.minutes || 0
                    : 0;

                  const totalMinutes =
                    storeOpenHour * 60 + storeOpenMinute + startBufferMinutes;
                  const hour = Math.floor(totalMinutes / 60);
                  const minute = totalMinutes % 60;

                  const displayHour =
                    hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
                  const ampm = hour >= 12 ? "PM" : "AM";

                  return `${displayHour}:${String(minute).padStart(
                    2,
                    "0"
                  )} ${ampm}`;
                })()}
              </span>
            </p>
            <p>
              • Last appointment:{" "}
              <span className="font-semibold text-green-700">
                {(() => {
                  const storeCloseHour = 20; // 8 PM
                  const storeCloseMinute = 0;
                  const endBufferMinutes = config.endBuffer?.enabled
                    ? config.endBuffer?.minutes || 0
                    : 0;

                  const totalMinutes =
                    storeCloseHour * 60 + storeCloseMinute - endBufferMinutes;
                  const hour = Math.floor(totalMinutes / 60);
                  const minute = totalMinutes % 60;

                  const displayHour =
                    hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
                  const ampm = hour >= 12 ? "PM" : "AM";

                  return `${displayHour}:${String(minute).padStart(
                    2,
                    "0"
                  )} ${ampm}`;
                })()}
              </span>
            </p>
          </div>
        </div>
      </div>

      {/* Start Buffer Settings */}
      <div className="bg-orange-50 rounded-lg p-6 space-y-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <SunIcon className="h-6 w-6 text-orange-600" />
            <div>
              <h4 className="font-medium">Start of Day Buffer</h4>
              <p className="text-sm text-gray-600">
                Time needed for store setup and preparation
              </p>
            </div>
          </div>
          {isEditMode ? (
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={config.startBuffer?.enabled || false}
                onChange={(e) =>
                  handleStartBufferChange("enabled", e.target.checked)
                }
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-orange-600 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
            </label>
          ) : (
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                config.startBuffer?.enabled
                  ? "bg-green-100 text-green-800"
                  : "bg-gray-100 text-gray-800"
              }`}
            >
              {config.startBuffer?.enabled ? "Enabled" : "Disabled"}
            </span>
          )}
        </div>

        {config.startBuffer?.enabled && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Buffer Duration
              </label>
              {isEditMode ? (
                <div className="flex items-center gap-2">
                  <NumberInput
                    value={config.startBuffer?.minutes || 30}
                    onChange={(value) =>
                      handleStartBufferChange("minutes", value)
                    }
                    min="0"
                    max="120"
                    step="5"
                    className="w-24 focus:ring-2 focus:ring-orange-500"
                  />
                  <span className="text-sm text-gray-600">minutes</span>
                </div>
              ) : (
                <p className="text-2xl font-bold text-orange-600">
                  {config.startBuffer?.minutes || 30} minutes
                </p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                No appointments will be available during this time after opening
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason / Description
              </label>
              {isEditMode ? (
                <input
                  type="text"
                  value={config.startBuffer?.reason || ""}
                  onChange={(e) =>
                    handleStartBufferChange("reason", e.target.value)
                  }
                  placeholder="e.g., Store setup, inventory check, team briefing"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                />
              ) : (
                <p className="text-sm text-gray-700">
                  {config.startBuffer?.reason || "Store setup and preparation"}
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* End Buffer Settings */}
      <div className="bg-indigo-50 rounded-lg p-6 space-y-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <MoonIcon className="h-6 w-6 text-indigo-600" />
            <div>
              <h4 className="font-medium">End of Day Buffer</h4>
              <p className="text-sm text-gray-600">
                Time needed for closing procedures
              </p>
            </div>
          </div>
          {isEditMode ? (
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={config.endBuffer?.enabled || false}
                onChange={(e) =>
                  handleEndBufferChange("enabled", e.target.checked)
                }
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-indigo-600 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
            </label>
          ) : (
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                config.endBuffer?.enabled
                  ? "bg-green-100 text-green-800"
                  : "bg-gray-100 text-gray-800"
              }`}
            >
              {config.endBuffer?.enabled ? "Enabled" : "Disabled"}
            </span>
          )}
        </div>

        {config.endBuffer?.enabled && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Buffer Duration
              </label>
              {isEditMode ? (
                <div className="flex items-center gap-2">
                  <NumberInput
                    value={config.endBuffer?.minutes || 45}
                    onChange={(value) =>
                      handleEndBufferChange("minutes", value)
                    }
                    min="0"
                    max="120"
                    step="5"
                    className="w-24 focus:ring-2 focus:ring-indigo-500"
                  />
                  <span className="text-sm text-gray-600">minutes</span>
                </div>
              ) : (
                <p className="text-2xl font-bold text-indigo-600">
                  {config.endBuffer?.minutes || 45} minutes
                </p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                No appointments will be available during this time before
                closing
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason / Description
              </label>
              {isEditMode ? (
                <input
                  type="text"
                  value={config.endBuffer?.reason || ""}
                  onChange={(e) =>
                    handleEndBufferChange("reason", e.target.value)
                  }
                  placeholder="e.g., Cash reconciliation, cleanup, security procedures"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              ) : (
                <p className="text-sm text-gray-700">
                  {config.endBuffer?.reason || "Closing procedures"}
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Apply to Days */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h4 className="font-medium mb-3">Apply Buffers to These Days</h4>
        <div className="flex flex-wrap gap-2">
          {daysOfWeek.map((day) => (
            <button
              key={day.value}
              onClick={() => isEditMode && handleApplyDaysChange(day.value)}
              disabled={!isEditMode}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                (config.applyToDays || []).includes(day.value)
                  ? "bg-blue-600 text-white"
                  : "bg-white text-gray-600 border-2 border-gray-200"
              } ${
                isEditMode
                  ? "hover:scale-105 cursor-pointer"
                  : "cursor-not-allowed opacity-75"
              }`}
            >
              {day.label}
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-2">
          {(config.applyToDays || []).length === 7
            ? "Buffers apply to all days"
            : `Buffers apply to ${(config.applyToDays || []).length} day(s)`}
        </p>
      </div>

      {/* Summary Card */}
      <div className="bg-white border-2 border-blue-200 rounded-lg p-6">
        <h4 className="font-medium mb-4 flex items-center gap-2">
          <ClockIcon className="h-5 w-5 text-blue-600" />
          Buffer Summary
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center p-3 bg-orange-50 rounded">
            <p className="text-sm text-gray-600">Start Buffer</p>
            <p className="text-2xl font-bold text-orange-600">
              {config.startBuffer?.enabled
                ? `${config.startBuffer?.minutes || 0} min`
                : "Off"}
            </p>
          </div>
          <div className="text-center p-3 bg-green-50 rounded">
            <p className="text-sm text-gray-600">Available Window</p>
            <p className="text-2xl font-bold text-green-600">
              {(() => {
                const totalMinutes = 480; // 8 hour day
                const startBuffer = config.startBuffer?.enabled
                  ? config.startBuffer?.minutes || 0
                  : 0;
                const endBuffer = config.endBuffer?.enabled
                  ? config.endBuffer?.minutes || 0
                  : 0;
                const available = totalMinutes - startBuffer - endBuffer;
                const hours = Math.floor(available / 60);
                const mins = available % 60;
                return `${hours}h ${mins}m`;
              })()}
            </p>
          </div>
          <div className="text-center p-3 bg-indigo-50 rounded">
            <p className="text-sm text-gray-600">End Buffer</p>
            <p className="text-2xl font-bold text-indigo-600">
              {config.endBuffer?.enabled
                ? `${config.endBuffer?.minutes || 0} min`
                : "Off"}
            </p>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-3 text-center">
          Based on typical 8-hour business day (480 minutes)
        </p>
      </div>
    </div>
  );
};

export default DayBuffersSettings;
